import { PERCENTUAL_SINAL } from './camadas.js';
import type { Candidato, CandidatoPatch } from './candidato.js';
import { formatarBRL } from './dinheiro.js';
import { enderecoPorExtenso, ENDERECO_VAZIO } from './endereco.js';
import { ocupaCadeira } from './etapas.js';
import { mascararDocumento, mascararTelefone } from './mascaras.js';

/**
 * O que pode e o que não pode mudar na ficha, conforme a esteira avança.
 *
 * Congelar tudo assim que a esteira começa seria errado: telefone e e-mail mudam, endereço
 * muda, e o Regimento **prevê** substituição de titular (art. 7º). Uma comunidade de doze
 * meses precisa de cadastro vivo.
 *
 * O que não pode mudar é **quem é a parte** e **qual é a cadeira**, depois de assinado:
 *
 *  - `tipo`, `doc` e `razao` identificam com quem o contrato foi firmado. Trocar depois da
 *    assinatura reescreve a outra parte de um instrumento já assinado — isso é aditivo
 *    contratual, não edição de cadastro.
 *  - `camada` congela na emissão da Carta, junto com os valores.
 *  - `numero` congela quando a cadeira passa a ocupada: é o lugar nominal do integrante.
 *
 * O resto segue editável, e **toda** alteração vai para a trilha com valor anterior e novo.
 */
export interface RestricaoDeEdicao {
  campo: keyof CandidatoPatch;
  rotulo: string;
  motivo: string;
}

/** Assinatura de qualquer instrumento congela a identidade da parte. */
export function identidadeEstaCongelada(candidato: Pick<Candidato, 'marcos'>): boolean {
  return Boolean(candidato.marcos.cartaAssinadaRecebida || candidato.marcos.termoAssinadoRecebido);
}

export function restricoesDeEdicao(
  candidato: Pick<Candidato, 'marcos' | 'etapa' | 'valoresContratados'>,
): RestricaoDeEdicao[] {
  const restricoes: RestricaoDeEdicao[] = [];

  if (identidadeEstaCongelada(candidato)) {
    const motivo =
      'Documento assinado identifica esta parte. Mudar exige aditivo contratual, ' +
      'não edição de cadastro.';
    restricoes.push(
      { campo: 'tipo', rotulo: 'Tipo de pessoa', motivo },
      { campo: 'doc', rotulo: 'CPF ou CNPJ', motivo },
      { campo: 'razao', rotulo: 'Razão social ou nome', motivo },
    );
  }

  if (candidato.valoresContratados) {
    restricoes.push({
      campo: 'camada',
      rotulo: 'Camada',
      motivo: 'Os valores do contrato foram congelados na emissão da Carta.',
    });
  }

  /*
   * Investimento anual e sinal são negociáveis até o link existir — e não um segundo
   * depois. Gerado o link, o valor está no gateway e pode já estar na mão do pagador:
   * mudá-lo aqui deixaria a ficha afirmando um número que ninguém vai receber. O total
   * fecha junto com o sinal porque é dele que o sinal e o saldo saem: mexer no
   * investimento depois do link move a mesma cobrança por via indireta. Para cobrar outro
   * valor, cancela-se a cobrança no Asaas e emite-se outra.
   */
  if (candidato.marcos.sinalLinkGerado) {
    const motivo =
      'O link do sinal já foi gerado sobre estes valores. Para cobrar outro, cancele a ' +
      'cobrança no gateway antes.';
    restricoes.push(
      { campo: 'sinalNegociado', rotulo: 'Valor do sinal', motivo },
      { campo: 'totalNegociado', rotulo: 'Investimento anual', motivo },
    );
  }

  if (ocupaCadeira(candidato.etapa)) {
    restricoes.push({
      campo: 'numero',
      rotulo: 'Número da cadeira',
      motivo: 'A cadeira está ocupada e é nominal.',
    });
  }

  return restricoes;
}

export function restricaoDoCampo(
  candidato: Pick<Candidato, 'marcos' | 'etapa' | 'valoresContratados'>,
  campo: keyof CandidatoPatch,
): RestricaoDeEdicao | undefined {
  return restricoesDeEdicao(candidato).find((r) => r.campo === campo);
}

// ─── Trilha de alterações ────────────────────────────────────────────────────

export interface Alteracao {
  campo: string;
  rotulo: string;
  de: string;
  para: string;
}

const ROTULOS: Record<string, string> = {
  tipo: 'Tipo de pessoa',
  razao: 'Razão social ou nome',
  doc: 'CPF ou CNPJ',
  endereco: 'Endereço',
  telefone: 'Telefone',
  representante: 'Representante legal',
  email: 'E-mail',
  titulares: 'Titulares',
  camada: 'Camada',
  numero: 'Número da cadeira',
  sinalNegociado: 'Valor do sinal',
  totalNegociado: 'Investimento anual',
  observacoes: 'Observações',
};

const VAZIO = '(em branco)';
/** Sinal sem negociação registrada: vale o percentual da tabela. */
const SINAL_DE_TABELA = `${PERCENTUAL_SINAL}% da tabela`;
/** Investimento sem negociação registrada: vale o valor da camada, ou o congelado. */
const TOTAL_DE_TABELA = 'valor de tabela da camada';

function comoTexto(campo: string, valor: unknown): string {
  // Antes do teste de vazio: negociado em branco não é "em branco", é o padrão da tabela —
  // e a trilha precisa dizer de qual valor para qual valor, não "de nada para R$ 6.000".
  if (campo === 'sinalNegociado') {
    return valor == null ? SINAL_DE_TABELA : formatarBRL(Number(valor));
  }

  if (campo === 'totalNegociado') {
    return valor == null ? TOTAL_DE_TABELA : formatarBRL(Number(valor));
  }

  if (valor === undefined || valor === null || valor === '') return VAZIO;

  if (campo === 'endereco') {
    const texto = enderecoPorExtenso({ ...ENDERECO_VAZIO, ...(valor as object) });
    return texto || VAZIO;
  }

  if (campo === 'titulares') {
    const lista = (valor as { nome?: string; cargo?: string }[])
      .filter((t) => t?.nome)
      .map((t) => (t.cargo ? `${t.nome} (${t.cargo})` : t.nome));
    return lista.length ? lista.join('; ') : VAZIO;
  }

  if (campo === 'doc') return mascararDocumento(String(valor));
  if (campo === 'telefone') return mascararTelefone(String(valor));

  return String(valor);
}

/**
 * Compara a ficha atual com o que está sendo salvo e descreve o que muda.
 *
 * Guardar só os nomes dos campos — "ficha atualizada: razao, doc" — não responde a
 * pergunta que a trilha existe para responder: **de quê para quê**. Sem isso, descobrir que
 * um CNPJ foi trocado depois da emissão não leva a lugar nenhum, porque o valor anterior
 * se perdeu.
 */
export function descreverAlteracoes(atual: Candidato, patch: CandidatoPatch): Alteracao[] {
  const alteracoes: Alteracao[] = [];

  for (const [campo, novo] of Object.entries(patch)) {
    if (novo === undefined) continue;

    const anterior = (atual as unknown as Record<string, unknown>)[campo];
    const de = comoTexto(campo, anterior);
    const para = comoTexto(campo, novo);
    if (de === para) continue;

    alteracoes.push({ campo, rotulo: ROTULOS[campo] ?? campo, de, para });
  }

  return alteracoes;
}

/** Uma linha por alteração, pronta para a linha do tempo. */
export function resumirAlteracoes(alteracoes: Alteracao[]): string {
  if (alteracoes.length === 0) return 'Ficha salva sem alterações.';
  return alteracoes.map((a) => `${a.rotulo}: "${a.de}" → "${a.para}"`).join(' · ');
}
